import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Pagination,
  Badge,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  QrCode as QrCodeIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  Flag as FlagIcon,
  EmojiPeople as SponsorIcon,
  TrendingUp as ReachIcon,
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { getAllCampaigns, getCampaignFullDetails } from '../../../api/campaigns/SabiShare';
import { getCampaignsBeneficiary } from '../../../api/analytics/analytics';

// Types
interface Beneficiary {
  beneficiary_phone: string;
  beneficiary_name?: string;
  redeemed_by?: string;
  voucher_id?: string;
  pin?: string;
  date_redeem?: string;
  campaign_name?: string;
  campaign_id?: string;
}

interface Campaign {
  donor_phone: string;
  id: number;
  campaign_id: string;
  name: string;
  join_code: string;
  user_id: string;
  description: string;
  budget: string;
  state: string;
  local_govt: string;
  sponsor_name: string;
  status: string;
  reach: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface FilterOptions {
  search: string;
}

// Beneficiary Details Modal Component
const BeneficiaryDetailsModal = ({ 
  beneficiary, 
  campaign, 
  open, 
  onClose 
}: { 
  beneficiary: Beneficiary | null; 
  campaign: Campaign | null; 
  open: boolean; 
  onClose: () => void;
}) => {
  const theme = useTheme();
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="h6">
            Beneficiary Details
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CancelIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                PERSONAL INFORMATION
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {beneficiary?.beneficiary_name || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 0.5, borderRadius: 0.5 }}>
                    {beneficiary?.beneficiary_phone || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.02), height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                CAMPAIGN INFORMATION
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Campaign Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {campaign?.name || beneficiary?.campaign_name || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Campaign ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), p: 0.5, borderRadius: 0.5 }}>
                    {campaign?.campaign_id || beneficiary?.campaign_id || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.02) }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                VOUCHER DETAILS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Voucher ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                      {beneficiary?.voucher_id}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PIN
                    </Typography>
                    <Typography variant="body1" fontWeight={700} letterSpacing={2}>
                      {beneficiary?.pin}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Redemption Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(beneficiary?.date_redeem)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label="Redeemed"
                        icon={<CheckCircleIcon />}
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.dark,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          size="small"
        >
          Export Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Campaign Details Card Component
const CampaignDetailsCard = ({ campaign }: { campaign: Campaign | null }) => {
  const theme = useTheme();
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  if (!campaign) return null;

  return (
    <Card sx={{ mb: 3, overflow: 'visible' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
            <CampaignIcon />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            Campaign Overview
          </Typography>
          <Chip
            size="small"
            label={campaign.status || 'active'}
            color={campaign.status?.toLowerCase() === 'active' ? 'success' : 'default'}
            sx={{ ml: 1 }}
          />
        </Stack>

        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12} md={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderColor: alpha(theme.palette.primary.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Campaign Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {campaign.name || 'N/A'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <SponsorIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Sponsor
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {campaign.sponsor_name?.trim() || 'Not specified'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Contact number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {campaign.donor_phone || 'No phone provided'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: alpha(theme.palette.info.main, 0.02),
                borderColor: alpha(theme.palette.info.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description & Location
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {campaign.description || 'No description'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FlagIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Local Government
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {campaign.local_govt || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <LocationIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    State
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {campaign.state || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Budget & Reach */}
          <Grid item xs={12} md={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: alpha(theme.palette.success.main, 0.02),
                borderColor: alpha(theme.palette.success.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Budget & Reach
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Budget
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {campaign.budget
                      ? `₦${parseFloat(campaign.budget).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : "₦0.00"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <ReachIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Target Reach
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="info.main">
                    {campaign.reach || 0} people
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.02),
                borderColor: alpha(theme.palette.warning.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Campaign Timeline
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DateRangeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(campaign.start_date)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DateRangeIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        End Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(campaign.end_date)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TimeIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(campaign.created_at)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(campaign.updated_at)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Main Component
const BeneficiaryReport = () => {
  const theme = useTheme();
  const history = useHistory();

  // Data states
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [filteredBeneficiaries, setFilteredBeneficiaries] = useState<Beneficiary[]>([]);

  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    search: ''
  });

  // UI states
  const [loading, setLoading] = useState({
    campaigns: false,
    beneficiaries: false
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch campaigns on initial load
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Update selected campaign when campaigns load or selection changes
  useEffect(() => {
    if (selectedCampaignId && campaigns.length > 0) {
      const campaign = campaigns.find(c => c.campaign_id === selectedCampaignId);
      setSelectedCampaign(campaign || null);
    } else {
      setSelectedCampaign(null);
    }
  }, [selectedCampaignId, campaigns]);

  // Fetch beneficiaries when campaign is selected
  useEffect(() => {
    if (selectedCampaignId) {
      fetchBeneficiaries(selectedCampaignId);
    } else {
      setBeneficiaries([]);
      setFilteredBeneficiaries([]);
    }
  }, [selectedCampaignId]);

  // Apply filters
  useEffect(() => {
    let filtered = [...beneficiaries];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(b => 
        b.beneficiary_name?.toLowerCase().includes(searchLower) ||
        b.pin?.toLowerCase().includes(searchLower) ||
        b.voucher_id?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredBeneficiaries(filtered);
    setPage(1);
  }, [filters, beneficiaries]);

  const fetchCampaigns = async () => {
    setLoading(prev => ({ ...prev, campaigns: true }));
    try {
      const response = await getAllCampaigns();
      // Handle the response structure based on your API
      const campaignsData = response?.data?.data || response?.data || response || [];
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError("Failed to load campaigns");
      showNotification("Failed to load campaigns", 'error');
    } finally {
      setLoading(prev => ({ ...prev, campaigns: false }));
    }
  };

  const fetchBeneficiaries = async (campaignId: string) => {
    setLoading(prev => ({ ...prev, beneficiaries: true }));
    try {
      const response = await getCampaignsBeneficiary(campaignId);
      // Handle the response structure based on your API
      const data = response?.data?.data || response?.data || response;
      const beneficiariesArray = Array.isArray(data) ? data : (data ? [data] : []);
      setBeneficiaries(beneficiariesArray);
      setFilteredBeneficiaries(beneficiariesArray);
      setError(null);
      
      if (beneficiariesArray.length === 0) {
        showNotification('No beneficiaries found for this campaign', 'info');
      }
    } catch (err) {
      console.error('Error fetching beneficiaries:', err);
      setError('Failed to load beneficiaries');
      showNotification('Failed to load beneficiaries', 'error');
      setBeneficiaries([]);
      setFilteredBeneficiaries([]);
    } finally {
      setLoading(prev => ({ ...prev, beneficiaries: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCampaigns();
    if (selectedCampaignId) {
      await fetchBeneficiaries(selectedCampaignId);
    }
    setRefreshing(false);
    showNotification('Data refreshed successfully', 'success');
  };

  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: ''
    });
  };

  const handleViewBeneficiary = (beneficiary: Beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setShowBeneficiaryModal(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate stats
  const stats = {
    totalBeneficiaries: beneficiaries.length,
    totalVouchersRedeemed: beneficiaries.length,
    lastRedemption: beneficiaries.length > 0 
      ? formatDate(beneficiaries[0]?.date_redeem)
      : 'No redemptions'
  };

  // Pagination
  const pageCount = Math.ceil(filteredBeneficiaries.length / rowsPerPage);
  const paginatedBeneficiaries = filteredBeneficiaries.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const activeFilterCount = filters.search ? 1 : 0;

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()} edge="start">
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <PeopleIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Beneficiary Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track and manage beneficiaries across campaigns
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="xl" sx={{ py: 3 }}>
            
            {/* Campaign Selection */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Campaign</InputLabel>
                    <Select
                      value={selectedCampaignId}
                      label="Select Campaign"
                      onChange={(e) => setSelectedCampaignId(e.target.value)}
                      disabled={loading.campaigns}
                    >
                      <MenuItem value="">
                        <em>Select a campaign</em>
                      </MenuItem>
                      {campaigns.map((campaign) => (
                        <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                            <CampaignIcon fontSize="small" color="primary" />
                            <Typography sx={{ flex: 1 }}>{campaign.name || 'Unnamed Campaign'}</Typography>
                            <Chip
                              size="small"
                              label={campaign.status || 'unknown'}
                              color={campaign.status?.toLowerCase() === 'active' ? 'success' : 'default'}
                              sx={{ ml: 1 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {selectedCampaignId && (
              <>
                {/* Campaign Details */}
                {selectedCampaign && <CampaignDetailsCard campaign={selectedCampaign} />}

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <PeopleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Beneficiaries
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalBeneficiaries}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Vouchers Redeemed
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalVouchersRedeemed}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                            <CalendarIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Last Redemption
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {stats.lastRedemption}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Badge badgeContent={activeFilterCount} color="primary">
                          <FilterIcon color="action" />
                        </Badge>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Filters
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                      </Button>
                    </Stack>
                  </Box>

                  {showFilters && (
                    <>
                      <Divider />
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Search by name, PIN, or voucher ID..."
                              value={filters.search}
                              onChange={(e) => handleFilterChange('search', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                                endAdornment: filters.search && (
                                  <InputAdornment position="end">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleFilterChange('search', '')}
                                      edge="end"
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Showing {filteredBeneficiaries.length} of {beneficiaries.length} beneficiaries
                          </Typography>
                          {filters.search && (
                            <Button
                              size="small"
                              onClick={clearFilters}
                              startIcon={<ClearIcon />}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Paper>

                {/* Beneficiaries Table */}
                <Paper>
                  <TableContainer>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Beneficiary</TableCell>
                          {/* <TableCell>Voucher ID</TableCell> */}
                          <TableCell>PIN</TableCell>
                          <TableCell>Redemption Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading.beneficiaries ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : paginatedBeneficiaries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                              <Stack spacing={2} alignItems="center">
                                <PeopleIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                                <Typography color="text.secondary">
                                  {filters.search ? 'No matching beneficiaries found' : 'No beneficiaries have redeemed vouchers yet'}
                                </Typography>
                                {filters.search && (
                                  <Button variant="outlined" size="small" onClick={clearFilters}>
                                    Clear Search
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedBeneficiaries.map((beneficiary, index) => (
                            <TableRow 
                              key={beneficiary.voucher_id || index}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleViewBeneficiary(beneficiary)}
                            >
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), fontSize: '0.875rem' }}>
                                    {beneficiary.beneficiary_name?.charAt(0) || '?'}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={500}>
                                    {beneficiary.beneficiary_name || 'Unknown'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              {/* <TableCell>
                                <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                  {beneficiary.voucher_id}
                                </Typography>
                              </TableCell> */}
                              <TableCell>
                                <Chip
                                  label={beneficiary.pin}
                                  size="small"
                                  icon={<QrCodeIcon />}
                                  sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.dark }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(beneficiary.date_redeem)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewBeneficiary(beneficiary);
                                    }}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {filteredBeneficiaries.length > 0 && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              </>
            )}

            {/* No Campaign Selected State */}
            {!selectedCampaignId && !loading.campaigns && (
              <Paper sx={{ py: 8, textAlign: 'center' }}>
                <Stack spacing={2} alignItems="center">
                  <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <CampaignIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Select a Campaign
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a campaign from the dropdown above to view its beneficiaries
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Beneficiary Details Modal */}
            <BeneficiaryDetailsModal
              beneficiary={selectedBeneficiary}
              campaign={selectedCampaign}
              open={showBeneficiaryModal}
              onClose={() => setShowBeneficiaryModal(false)}
            />

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default BeneficiaryReport;